import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/useAuth';
import type { ResolvedUserConfig } from '../types/userConfig';
import { useUserConfigQuery } from './queries/useUserConfigQuery';
import {
  clearUserConfigBootstrapCache,
  resolveBootstrapUserConfig,
  writeUserConfigBootstrapCache,
} from './userConfigBootstrapCache';
import { UserConfigContext } from './userConfigContext';
import { applyUserConfigToDom, resolveUserConfig } from './userConfigModel';
import { useUserConfigActions } from './useUserConfigActions';
import {
  getUserConfigSessionKey,
  getUserConfigStatus,
  mergeRemoteUserConfig,
  type UserConfigSessionKey,
} from './userConfigSession';

export type { UserConfigStatus } from './userConfigSession';

/** Survives React Strict Mode remounts (unlike a ref reset on remount). */
const pageSizeSeedAttemptedSessions = new Set<UserConfigSessionKey>();

export function UserConfigProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const sessionKey = getUserConfigSessionKey(isLoading, isAuthenticated);
  const configQuery = useUserConfigQuery();

  const [config, setConfig] = useState<ResolvedUserConfig>(
    resolveBootstrapUserConfig,
  );
  const [appliedSessionKey, setAppliedSessionKey] =
    useState<UserConfigSessionKey | null>(null);

  const status = getUserConfigStatus(sessionKey, configQuery);
  const isUserConfigReady = status === 'ready';

  const { value, updateConfigMutation } = useUserConfigActions({
    isAuthenticated,
    config,
    isUserConfigReady,
    setConfig,
  });
  // useMutation() return identity changes every render — only `mutate` is stable.
  const updateConfigMutate = updateConfigMutation.mutate;

  if (sessionKey === null && appliedSessionKey !== null) {
    setAppliedSessionKey(null);
  }

  if (sessionKey === 'auth' && appliedSessionKey !== 'auth') {
    if (configQuery.isSuccess) {
      const partial = configQuery.data;
      setConfig((current) => mergeRemoteUserConfig(current, partial));
      setAppliedSessionKey('auth');
    } else if (configQuery.isError) {
      setConfig(resolveUserConfig());
      setAppliedSessionKey('auth');
    }
  }

  useEffect(() => {
    applyUserConfigToDom(config);
  }, [config]);

  useEffect(() => {
    if (!isUserConfigReady) {
      return;
    }

    writeUserConfigBootstrapCache({
      theme: config.theme,
      locale: config.locale,
      knowledgeDocumentsPageSize: config.knowledgeDocumentsPageSize,
      galleryImagesPageSize: config.galleryImagesPageSize,
      chatbotConversationsPageSize: config.chatbotConversationsPageSize,
      chatbotTrainingExamplesPageSize: config.chatbotTrainingExamplesPageSize,
      pinnedKnowledgeDatabaseIds: config.pinnedKnowledgeDatabaseIds,
      replacePlatformChatDraftTextOnTranscription:
        config.replacePlatformChatDraftTextOnTranscription,
      keepSidebarOpen: config.keepSidebarOpen,
      platformChatMaxResults: config.platformChatMaxResults,
      platformChatMinScore: config.platformChatMinScore,
    });
  }, [config, isUserConfigReady]);

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    clearUserConfigBootstrapCache();
    pageSizeSeedAttemptedSessions.clear();
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (appliedSessionKey !== 'auth' || !configQuery.isSuccess) {
      return;
    }

    const missingDocumentsPageSize =
      configQuery.data.knowledgeDocumentsPageSize == null;
    const missingGalleryPageSize =
      configQuery.data.galleryImagesPageSize == null;
    const missingChatbotConversationsPageSize =
      configQuery.data.chatbotConversationsPageSize == null;
    const missingChatbotTrainingExamplesPageSize =
      configQuery.data.chatbotTrainingExamplesPageSize == null;

    if (
      !missingDocumentsPageSize &&
      !missingGalleryPageSize &&
      !missingChatbotConversationsPageSize &&
      !missingChatbotTrainingExamplesPageSize
    ) {
      return;
    }

    if (pageSizeSeedAttemptedSessions.has('auth')) {
      return;
    }

    pageSizeSeedAttemptedSessions.add('auth');
    updateConfigMutate({
      ...(missingDocumentsPageSize
        ? {
            knowledgeDocumentsPageSize: config.knowledgeDocumentsPageSize,
          }
        : {}),
      ...(missingGalleryPageSize
        ? { galleryImagesPageSize: config.galleryImagesPageSize }
        : {}),
      ...(missingChatbotConversationsPageSize
        ? {
            chatbotConversationsPageSize: config.chatbotConversationsPageSize,
          }
        : {}),
      ...(missingChatbotTrainingExamplesPageSize
        ? {
            chatbotTrainingExamplesPageSize:
              config.chatbotTrainingExamplesPageSize,
          }
        : {}),
    });
  }, [
    appliedSessionKey,
    config.chatbotConversationsPageSize,
    config.chatbotTrainingExamplesPageSize,
    config.galleryImagesPageSize,
    config.knowledgeDocumentsPageSize,
    configQuery.data,
    configQuery.isSuccess,
    updateConfigMutate,
  ]);

  return (
    <UserConfigContext.Provider value={value}>
      {children}
    </UserConfigContext.Provider>
  );
}
